import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PhoneOutgoing, PhoneIncoming } from "lucide-react";
import { Field } from "@/components/inquiries/InquiryFormDialog";
import { notifyInquiriesChanged } from "@/lib/inquiry";

const blank = { direction: "outgoing", outcome: "", notes: "", followup_date: "" };

export const LogCallDialog = ({ inquiry, settings, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (inquiry) setF(blank); }, [inquiry]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const type = settings?.interaction_types.includes("Call") ? "Call" : settings?.interaction_types[0];
  const channel = settings?.channels.find((c) => /phone/i.test(c)) || null;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/inquiries/${inquiry.id}/events`, { at: new Date().toISOString(), type, channel, direction: f.direction,
        outcome: f.outcome || null, notes: f.notes || null, followup_date: f.followup_date || null, contact_ids: [] });
      toast.success(`Call logged on ${inquiry.number}`);
      notifyInquiriesChanged(); onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const DirBtn = ({ v, icon: Icon, label }) => (
    <button type="button" data-testid={`logcall-dir-${v}`} onClick={() => set("direction")(v)}
      className={`flex-1 inline-flex items-center justify-center gap-2 text-sm py-2 rounded-md border transition-colors ${f.direction === v ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-700 hover:border-blue-300"}`}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );
  return (
    <Dialog open={!!inquiry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="logcall-dialog">
        <DialogHeader><DialogTitle>Log call · <span className="mono text-blue-700 text-base">{inquiry?.number}</span></DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 -mt-2 truncate">{inquiry?.subject}</div>
        <div className="space-y-3">
          <div className="flex gap-2"><DirBtn v="outgoing" icon={PhoneOutgoing} label="I called them" /><DirBtn v="incoming" icon={PhoneIncoming} label="They called" /></div>
          <Field label="Outcome"><Input data-testid="logcall-outcome" autoFocus value={f.outcome} onChange={set("outcome")} placeholder="e.g. Asked to call back Monday" /></Field>
          <Field label="Notes"><Textarea data-testid="logcall-notes" rows={3} value={f.notes} onChange={set("notes")} /></Field>
          <Field label="Next follow-up (optional)"><Input data-testid="logcall-followup" type="date" value={f.followup_date} onChange={set("followup_date")} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="logcall-cancel">Cancel</Button>
          <Button data-testid="logcall-save" disabled={busy || (!f.outcome.trim() && !f.notes.trim())} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">{busy ? "Saving…" : "Save call"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
